import { Search, X } from 'lucide-react'
import { useRef } from 'react'

/**
 * Compact search input with a leading magnifier icon and (since #1213)
 * a trailing clear-X when the value is non-empty. Escape inside the
 * input clears as well — Escape-empty falls through so a parent modal
 * can still close on it.
 *
 * The clear affordance defaults on: every consumer of `SearchInput`
 * is a literal search field where "clear and start over" is the
 * canonical action. The opt-out exists for completeness, not because
 * we expect anyone to want it.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  clearable = true,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  clearable?: boolean
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const hasValue = clearable && value.length > 0

  const clear = () => {
    onChange('')
    inputRef.current?.focus()
  }

  return (
    <div className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
      <input
        ref={inputRef}
        // Reserve room on the right for the X unconditionally when
        // clearable so the caret doesn't jump as the first character
        // is typed / the last character is deleted.
        className={`w-full text-xs bg-background border border-border rounded-md pl-7 ${
          clearable ? 'pr-6' : 'pr-2'
        } py-1.5 focus:outline-hidden focus:border-primary/50`}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (clearable && e.key === 'Escape' && value.length > 0) {
            e.preventDefault()
            e.stopPropagation()
            clear()
          }
        }}
      />
      {hasValue && (
        <button
          type="button"
          aria-label="Clear"
          onClick={clear}
          onMouseDown={e => e.preventDefault()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              clear()
            }
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}
